import { useCallback, useEffect, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import type { MessageAttachment, TicketMessage, UserInfo } from '@/entities/ticket';
import { acquireSocket, releaseSocket, joinTicketRoom, type MessageKind } from '@/shared/api';
import { ticketsApi } from '@/shared/api/tickets';
import { TYPING_TIMEOUT_MS } from './chat-lib';
import { belongsToFeed, mergeFetchedMessages, mergeReads } from './chat-sync';

export interface TicketReader {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  lastReadAt: string;
}

export function useChatSocket({
  ticketId,
  currentUserId,
  setMessages,
  onMessageDeleted,
  kind = 'chat',
  onNotesRead,
}: {
  ticketId: string;
  currentUserId: string;
  setMessages: React.Dispatch<React.SetStateAction<TicketMessage[]>>;
  onMessageDeleted: (messageId: string) => void;
  kind?: MessageKind;
  onNotesRead?: (lastReadAt: string) => void;
}) {
  const isNotes = kind === 'notes';
  const onNotesReadRef = useRef(onNotesRead);
  onNotesReadRef.current = onNotesRead;
  const [typingUserId, setTypingUserId] = useState<string | null>(null);
  const [reads, setReads] = useState<Record<string, TicketReader>>({});
  const socketRef = useRef<Socket | null>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingStopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onDeletedRef = useRef(onMessageDeleted);
  onDeletedRef.current = onMessageDeleted;

  useEffect(() => {
    if (!ticketId) return;
    let cancelled = false;
    setReads({});
    setTypingUserId(null);

    const onMessage = (payload: TicketMessage) => {
      if (payload.ticketId !== ticketId) return;
      if (!belongsToFeed(payload, kind)) return;
      setMessages((prev) => {
        if (prev.some((m) => m.id === payload.id)) return prev;
        return [...prev, payload];
      });
    };

    const onMessageUpdated = (payload: {
      id: string;
      ticketId: string;
      body?: string;
      editedAt?: string;
      attachments?: MessageAttachment[];
      mentions?: UserInfo[];
    }) => {
      if (payload.ticketId !== ticketId) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === payload.id
            ? {
                ...m,
                ...(payload.body !== undefined ? { body: payload.body } : {}),
                ...(payload.editedAt !== undefined ? { editedAt: payload.editedAt } : {}),
                ...(payload.attachments !== undefined ? { attachments: payload.attachments } : {}),
                ...(payload.mentions !== undefined ? { mentions: payload.mentions } : {}),
              }
            : m,
        ),
      );
    };

    const onMessageDeletedWs = (payload: { id: string; ticketId: string }) => {
      if (payload.ticketId !== ticketId) return;
      setMessages((prev) => prev.filter((m) => m.id !== payload.id));
      onDeletedRef.current(payload.id);
    };

    const onTypingStart = (payload: { userId: string }) => {
      if (isNotes || payload.userId === currentUserId) return;
      setTypingUserId(payload.userId);
      if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = setTimeout(() => setTypingUserId(null), TYPING_TIMEOUT_MS);
    };

    const onTypingStop = (payload: { userId: string }) => {
      if (payload.userId === currentUserId) return;
      setTypingUserId((id) => (id === payload.userId ? null : id));
    };

    const onTicketRead = (payload: {
      ticketId: string;
      userId: string;
      displayName?: string;
      lastReadAt: string;
    }) => {
      if (payload.ticketId !== ticketId) return;
      setReads((prev) => {
        const known = prev[payload.userId];
        if (known && known.lastReadAt >= payload.lastReadAt) return prev;
        return {
          ...prev,
          [payload.userId]: {
            userId: payload.userId,
            displayName: payload.displayName ?? known?.displayName ?? 'Участник',
            lastReadAt: payload.lastReadAt,
          },
        };
      });
    };

    const onNotesReadWs = (payload: { ticketId: string; lastReadAt: string }) => {
      if (payload.ticketId !== ticketId) return;
      onNotesReadRef.current?.(payload.lastReadAt);
    };

    if (!isNotes) {
      void ticketsApi
        .getReads(ticketId)
        .then((res) => {
          if (cancelled) return;
          const map: Record<string, TicketReader> = {};
          for (const r of res.data ?? []) map[r.userId] = r;
          setReads((prev) => mergeReads(map, Object.values(prev)));
        })
        .catch(() => {});
    }

    const resync = () => {
      void ticketsApi
        .getMessages(ticketId, { kind })
        .then((res) => {
          if (cancelled || !Array.isArray(res.data)) return;
          const fetched = res.data;
          setMessages((prev) => mergeFetchedMessages(prev, fetched));
        })
        .catch(() => {});
      if (isNotes) return;
      void ticketsApi
        .getReads(ticketId)
        .then((res) => {
          if (cancelled) return;
          setReads((prev) => mergeReads(prev, res.data ?? []));
        })
        .catch(() => {});
    };
    let wasConnected = false;
    const onConnect = () => {
      if (wasConnected) resync();
      wasConnected = true;
    };

    const leaveRoom = joinTicketRoom(ticketId);
    void acquireSocket().then((socket) => {
      if (cancelled || !socket) return;
      socketRef.current = socket;
      wasConnected = socket.connected;
      socket.on('message', onMessage);
      socket.on('message_updated', onMessageUpdated);
      socket.on('message_deleted', onMessageDeletedWs);
      socket.on('typing_start', onTypingStart);
      socket.on('typing_stop', onTypingStop);
      socket.on('ticket_read', onTicketRead);
      socket.on('ticket_notes_read', onNotesReadWs);
      socket.on('connect', onConnect);
    });

    return () => {
      cancelled = true;
      const socket = socketRef.current;
      if (socket) {
        socket.off('message', onMessage);
        socket.off('message_updated', onMessageUpdated);
        socket.off('message_deleted', onMessageDeletedWs);
        socket.off('typing_start', onTypingStart);
        socket.off('typing_stop', onTypingStop);
        socket.off('ticket_read', onTicketRead);
        socket.off('ticket_notes_read', onNotesReadWs);
        socket.off('connect', onConnect);
        socketRef.current = null;
      }
      leaveRoom();
      releaseSocket();
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
    };
  }, [ticketId, currentUserId, setMessages, kind, isNotes]);

  const emitTypingStart = useCallback(() => {
    const socket = socketRef.current;
    if (isNotes || !socket?.connected) return;
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    socket.emit('typing_start', ticketId);
    typingTimerRef.current = setTimeout(() => {
      socket.emit('typing_stop', ticketId);
      typingTimerRef.current = null;
    }, 2000);
  }, [ticketId, isNotes]);

  const emitTypingStop = useCallback(() => {
    const socket = socketRef.current;
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    if (!isNotes && socket?.connected) socket.emit('typing_stop', ticketId);
  }, [ticketId, isNotes]);

  const lastSentReadRef = useRef<string | null>(null);
  const markRead = useCallback(
    (readAt: string) => {
      const socket = socketRef.current;
      if (!socket?.connected) return;
      if (lastSentReadRef.current && lastSentReadRef.current >= readAt) return;
      lastSentReadRef.current = readAt;
      socket.emit(isNotes ? 'mark_ticket_notes_read' : 'mark_ticket_read', { ticketId, readAt });
    },
    [ticketId, isNotes],
  );

  return { typingUserId, emitTypingStart, emitTypingStop, reads, markRead };
}
